import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  Save, Sparkles, ChevronDown, ChevronUp, Loader2, AlertTriangle,
  Info, FileText, Wrench, Lightbulb, TrendingUp, TrendingDown,
  Plus, Trash2, Users
} from 'lucide-react';
import axios from 'axios';

import { API_URL } from '../../utils/api';

const EVALUATOR_ROLES = [
  'School Psychologist', 'SLP', 'Special Education Teacher',
  'Occupational Therapist', 'Physical Therapist', 'General Education Teacher',
  'Board Certified Behavior Analyst', 'Other',
];

const OVERALL_FIELDS = [
  { key: 'summary', label: 'Summary', icon: FileText, hint: 'Overall summary of evaluation findings.', rows: 6 },
  { key: 'conclusion', label: 'Conclusion', icon: FileText, hint: 'Conclusions drawn from the evaluation data.', rows: 5 },
  { key: 'service_modifications', label: 'Consideration of Additions or Modifications to Services', icon: Wrench, hint: 'What services may need to be added or changed?', rows: 3 },
  { key: 'assistive_technology', label: 'Assistive Technology', icon: Wrench, hint: 'Assistive technology needs and recommendations.', rows: 3 },
  { key: 'recommendations', label: 'Recommendations and Identification of Needs', icon: Lightbulb, hint: 'Specific recommendations for academic and behavioral needs.', rows: 4 },
];

function mergeContent(existing) {
  const defaults = { evaluator_contributions: [] };
  OVERALL_FIELDS.forEach(f => { defaults[f.key] = ''; });
  if (!existing || typeof existing !== 'object') return defaults;
  if (existing.notes && !existing.summary) return { ...defaults, summary: existing.notes };
  return { ...defaults, ...existing };
}

export default function SummaryConclusionEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  const [helperOpen, setHelperOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [helperData, setHelperData] = useState(null);
  const [helperMeta, setHelperMeta] = useState(null);

  useEffect(() => { setContent(mergeContent(section?.content)); }, [section?.section_id]);

  const updateField = (key, value) => setContent(prev => ({ ...prev, [key]: value }));
  const handleSave = () => onSave(content);
  const contributions = content.evaluator_contributions || [];

  const addContribution = () => {
    setContent(prev => ({
      ...prev,
      evaluator_contributions: [
        ...(prev.evaluator_contributions || []),
        { id: `contrib_${Date.now()}`, name: '', role: '', contribution: '' },
      ],
    }));
  };

  const removeContribution = (idx) => {
    setContent(prev => ({
      ...prev,
      evaluator_contributions: prev.evaluator_contributions.filter((_, i) => i !== idx),
    }));
  };

  const updateContribution = (idx, field, value) => {
    setContent(prev => ({
      ...prev,
      evaluator_contributions: prev.evaluator_contributions.map((item, i) =>
        i === idx ? { ...item, [field]: value } : item
      ),
    }));
  };

  const runHelper = async () => {
    setAnalyzing(true);
    setHelperData(null);
    setHelperMeta(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/iep-met/cases/${caseData.case_id}/met-sections/met_summary_conclusion/draft`,
        {}, { withCredentials: true }
      );
      const output = res.data?.output;
      if (output) {
        setHelperData(output);
        setHelperMeta({
          dataSources: output.data_sources_used || [],
          notes: output.notes || '',
          model: res.data?.model_used,
        });
      }
    } catch (err) {
      console.error('AI helper failed', err);
      setHelperMeta({ error: err.response?.data?.detail || 'AI analysis failed. Please try again.' });
    } finally {
      setAnalyzing(false);
    }
  };

  const filledOverall = OVERALL_FIELDS.filter(f => (content[f.key] || '').trim()).length;

  return (
    <div className="max-w-4xl space-y-6" data-testid="summary-conclusion-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="summary-conclusion-title">Summary / Conclusion</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-[#5A8A5A]/10 text-[#5A8A5A] text-xs">{contributions.length} evaluator{contributions.length !== 1 ? 's' : ''}</Badge>
            <Badge className="bg-[#F3F4F1] text-[#595959] text-xs">{filledOverall}/{OVERALL_FIELDS.length} overall fields</Badge>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl shrink-0" data-testid="summary-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* AI Analysis Helper Panel */}
      <div className="bg-gradient-to-r from-[#5A8A5A]/5 to-[#3B6A9B]/5 border border-[#5A8A5A]/20 rounded-2xl overflow-hidden" data-testid="ai-summary-helper">
        <button className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/40 transition-colors" onClick={() => setHelperOpen(!helperOpen)} data-testid="summary-helper-toggle">
          <div className="flex items-center gap-2.5">
            <Sparkles className="h-4 w-4 text-[#5A8A5A]" />
            <span className="text-sm font-medium text-[#1C1C1C]">AI Analysis Helper</span>
            <Badge className="text-[10px] bg-[#5A8A5A]/10 text-[#5A8A5A] border border-[#5A8A5A]/20">Helper Only</Badge>
          </div>
          {helperOpen ? <ChevronUp className="h-4 w-4 text-[#595959]" /> : <ChevronDown className="h-4 w-4 text-[#595959]" />}
        </button>

        {helperOpen && (
          <div className="px-5 pb-5 border-t border-[#5A8A5A]/10">
            <div className="flex items-center justify-between mt-3 mb-4">
              <p className="text-xs text-[#595959] max-w-lg">
                AI surfaces key findings, themes, and recommended points to include. Use this as a reference.
              </p>
              <Button onClick={runHelper} disabled={analyzing}
                className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl text-xs h-8 shrink-0" data-testid="run-summary-helper-btn">
                {analyzing ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Analyzing...</>
                  : <><Sparkles className="h-3 w-3 mr-1.5" />{helperData ? 'Re-analyze' : 'Analyze Data'}</>}
              </Button>
            </div>

            {helperMeta?.error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="summary-helper-error">
                <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" /><p className="text-xs text-red-700">{helperMeta.error}</p>
              </div>
            )}

            {helperData && (
              <div className="space-y-4" data-testid="summary-helper-results">
                {helperData.overall_picture && (
                  <div className="bg-white border border-[#E5E5E5] rounded-xl p-3" data-testid="helper-overall">
                    <h4 className="text-xs font-bold text-[#595959] mb-1.5">Overall Picture</h4>
                    <p className="text-xs text-[#1C1C1C]">{helperData.overall_picture}</p>
                  </div>
                )}
                {helperData.key_findings?.length > 0 && (
                  <div className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid="helper-findings">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-2">Key Findings</h4>
                    <div className="space-y-2">{helperData.key_findings.map((f, i) => (
                      <div key={i} className="border-l-2 border-[#5A8A5A]/30 pl-3 py-1"><p className="text-xs font-medium text-[#1C1C1C]">{f.area}: {f.finding}</p>{f.implication && <p className="text-[10px] text-[#595959] italic">{f.implication}</p>}</div>
                    ))}</div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {helperData.strengths_to_highlight?.length > 0 && (
                    <div className="bg-green-50/50 border border-green-200 rounded-xl p-3" data-testid="helper-strengths">
                      <h4 className="text-xs font-bold text-green-700 mb-1.5 flex items-center gap-1"><TrendingUp className="h-3 w-3" />Strengths</h4>
                      <ul className="space-y-0.5">{helperData.strengths_to_highlight.map((s, i) => <li key={i} className="text-xs text-green-800">- {s}</li>)}</ul>
                    </div>
                  )}
                  {helperData.needs_to_address?.length > 0 && (
                    <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-3" data-testid="helper-needs">
                      <h4 className="text-xs font-bold text-amber-700 mb-1.5 flex items-center gap-1"><TrendingDown className="h-3 w-3" />Needs</h4>
                      <ul className="space-y-0.5">{helperData.needs_to_address.map((n, i) => <li key={i} className="text-xs text-amber-800">- {n}</li>)}</ul>
                    </div>
                  )}
                </div>
                {helperData.recommended_summary_points?.length > 0 && (
                  <div className="bg-[#F3F4F1] border border-[#E5E5E5] rounded-xl p-3" data-testid="helper-summary-points">
                    <h4 className="text-xs font-bold text-[#595959] mb-1.5">Recommended Summary Points</h4>
                    <ul className="space-y-0.5">{helperData.recommended_summary_points.map((p, i) => <li key={i} className="text-xs text-[#1C1C1C]">- {p}</li>)}</ul>
                  </div>
                )}
                {helperData.recommended_conclusion_points?.length > 0 && (
                  <div className="bg-[#F3F4F1] border border-[#E5E5E5] rounded-xl p-3" data-testid="helper-conclusion-points">
                    <h4 className="text-xs font-bold text-[#595959] mb-1.5">Recommended Conclusion Points</h4>
                    <ul className="space-y-0.5">{helperData.recommended_conclusion_points.map((p, i) => <li key={i} className="text-xs text-[#1C1C1C]">- {p}</li>)}</ul>
                  </div>
                )}
                {helperData.service_considerations?.length > 0 && (
                  <div className="bg-white border border-[#E5E5E5] rounded-xl p-3" data-testid="helper-services">
                    <h4 className="text-xs font-bold text-[#595959] mb-1.5">Service Considerations</h4>
                    {helperData.service_considerations.map((s, i) => (<div key={i} className="mb-1 last:mb-0"><p className="text-xs text-[#1C1C1C]">{s.consideration}</p><p className="text-[10px] text-[#595959] italic">{s.basis}</p></div>))}
                  </div>
                )}
                {helperMeta && !helperMeta.error && (
                  <div className="text-[10px] text-[#595959]/60 flex items-center gap-2"><Info className="h-3 w-3" /><span>Sources: {helperMeta.dataSources?.join(', ') || 'none'}</span></div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Evaluator-Specific Contributions */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-3 px-1">Evaluator Contributions</h3>
        {contributions.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 text-center">
            <Users className="h-8 w-8 text-[#E5E5E5] mx-auto mb-3" />
            <p className="text-sm text-[#595959] mb-4">No evaluator contributions added yet.</p>
            <Button variant="outline" className="rounded-xl" onClick={addContribution} data-testid="add-first-contribution">
              <Plus className="h-4 w-4 mr-2" />Add Evaluator Contribution
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {contributions.map((item, idx) => (
              <div key={item.id || idx} className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid={`evaluator-contrib-${idx}`}>
                <div className="flex items-center justify-between px-5 py-3.5 bg-[#F3F4F1]">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#5A8A5A]" />
                    <span className="text-sm font-medium text-[#1C1C1C]">Evaluator {idx + 1}</span>
                    {item.name && <span className="text-xs text-[#595959]">— {item.name}</span>}
                  </div>
                  <button onClick={() => removeContribution(idx)} className="p-1.5 rounded-lg hover:bg-red-50 transition" data-testid={`remove-contrib-${idx}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </button>
                </div>
                <div className="p-5 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Evaluator Name</label>
                      <Input value={item.name || ''} onChange={e => updateContribution(idx, 'name', e.target.value)}
                        placeholder="e.g., Dr. Sarah Martinez" className="rounded-xl border-[#E5E5E5] text-sm h-9"
                        data-testid={`contrib-name-${idx}`} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Role / Credentials</label>
                      <Select value={item.role || ''} onValueChange={v => updateContribution(idx, 'role', v)}>
                        <SelectTrigger className="rounded-xl text-sm h-9" data-testid={`contrib-role-${idx}`}><SelectValue placeholder="Select role" /></SelectTrigger>
                        <SelectContent>{EVALUATOR_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Summary / Conclusion Contribution</label>
                    <Textarea value={item.contribution || ''} onChange={e => updateContribution(idx, 'contribution', e.target.value)}
                      placeholder="Provide your summary and conclusions from your area of expertise..."
                      rows={6} className="rounded-xl border-[#E5E5E5] text-sm"
                      data-testid={`contrib-text-${idx}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3">
          <Button variant="outline" className="rounded-xl" onClick={addContribution} data-testid="add-contribution-btn">
            <Plus className="h-4 w-4 mr-2" />Add Evaluator Contribution
          </Button>
        </div>
      </div>

      {/* Overall Summary/Conclusion Fields */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-3 px-1">Overall Summary & Conclusion</h3>
        <div className="space-y-4">
          {OVERALL_FIELDS.map(field => {
            const Icon = field.icon;
            return (
              <div key={field.key} className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid={`summary-field-${field.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-4 w-4 text-[#5A8A5A] shrink-0" />
                  <label className="text-sm font-medium text-[#1C1C1C]">{field.label}</label>
                  {(content[field.key] || '').trim() && <span className="ml-auto text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Completed</span>}
                </div>
                <p className="text-xs text-[#595959] mb-3 ml-6">{field.hint}</p>
                <Textarea
                  value={content[field.key] || ''} onChange={e => updateField(field.key, e.target.value)}
                  placeholder={field.hint} rows={field.rows}
                  className="rounded-xl border-[#E5E5E5] text-sm"
                  data-testid={`summary-input-${field.key}`}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl" data-testid="summary-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Summary / Conclusion'}
        </Button>
      </div>
    </div>
  );
}
